import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Checkbox, Dropdown, Icon, Groupper, Header, Input, Table, Message } from 'react-frontier';
import { SurveyQuestionType, SurveyQuestionValue, SetLoading } from '../SiteClasses';
import { bindFormChange, surveyTypeHasValues } from '../SiteUtil';
import { useBreadcrumbs } from '../Hooks';
import Validator from '../Validator';
import Toast from '../Toast';
import API from '../API';

interface QuestionValueForm extends SurveyQuestionValue{
	edit_mode?: boolean,
	[key: string]: any,
}

interface QuestionForm{
	question_id?: number,
	type_id: SurveyQuestionType,
	question: string,
	question_comment: string,
	default_value: string,
	required: boolean,
	values?: QuestionValueForm[],
	deleted: boolean,
}

var SurveyQuestion = ()=>{
	var params = useParams<{ qid: string }>();
	var question_id = params.qid;
	var IS_CREATE = question_id === 'create';

	var navigate = useNavigate();
	var { setTitle } = useBreadcrumbs();
	var [form, setForm] = useState<QuestionForm>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [error, setError] = useState<string>(null);

	useEffect(()=>{
		setTitle(IS_CREATE ? 'Crear pregunta' : `Pregunta #${question_id}`);
		get();
	}, [question_id]);

	var get = ()=>{
		if(!IS_CREATE && !Number.isNaN(parseInt(question_id))){
			API.getSurveyQuestion(parseInt(question_id)).then(res=>{
				if(res.error) return setLoadError(res.message);
				setForm(res.data);
			}).catch(()=>{
				setLoadError('Hubo un error cargando la pregunta (SVQ-1)');
			});
		}else{
			setForm({
				question: '',
				type_id: null,
				question_comment: '',
				default_value: '',
				required: false,
				deleted: false,
			});
		}
	}

	var save = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			question: [{ rule: 'length', params: [3, 128], label: 'Pregunta' }],
			type_id: [{ rule: 'empty', label: 'Tipo de pregunta' }],
			question_comment: [{ rule: 'maxLength', params: [128], label: 'Comentario' }],
			default_value: [{ rule: 'maxLength', params: [128], label: 'Valor por defecto' }],
		});
		setError(valid ? null : prompts[0]);
		if(!valid) return;
		setLoading(true);
		if(!IS_CREATE){
			API.editSurveyQuestion(parseInt(question_id), form.type_id, form.question, form.question_comment, form.default_value, form.required).then(res=>{
				if(res.error) return setError(res.message);
				if(res.data.replaced){
					Toast.success('La pregunta ya tenía respuestas, se reemplazó por una nueva para no alterar el historial');
					navigate(`/surveys/${res.data.question_id}`);
				}else{
					Toast.success('Pregunta actualizada exitosamente');
				}
			}).catch(()=>{
				setError('Hubo un error inesperado actualizando la pregunta (SVQ-11)');
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.createSurveyQuestion(form.type_id, form.question, form.question_comment, form.default_value, form.required).then(res=>{
				if(res.error) return setError(res.message);
				Toast.success('Pregunta creada exitosamente');
				navigate(`/surveys/${res.data}`);
			}).catch(()=>{
				setError('Hubo un error inesperado creando la pregunta (SVQ-12)');
			}).finally(()=>{
				setLoading(false);
			});
		}
	}

	var onDelete = (setLoading: SetLoading)=>{
		setError(null);
		setLoading(true);
		API.deleteSurveyQuestion(parseInt(question_id), !form.deleted).then(res=>{
			if(res.error) return setError(res.message);
			Toast.success(form.deleted ? 'Pregunta restaurada exitosamente' : 'Pregunta eliminada exitosamente');
			setForm(q=>({ ...q, deleted: !q.deleted }));
		}).catch(()=>{
			setError('Hubo un error inesperado (SVQ-13)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onOptionChange = (index: number, label: string)=>{
		return (value: any)=>{
			var q = { ...form, values: [...form.values] };
			q.values[index][label] = value;
			setForm(q);
		}
	}

	var addOption = ()=>{
		var q = { ...form, values: form.values ? [...form.values] : [] };
		q.values.push({ value_id: null, question_id: form.question_id, value: '', edit_mode: true });
		setForm(q);
	}

	var onSaveOption = (option: QuestionValueForm)=>{
		return (setLoading: SetLoading)=>{
			var { valid, prompts } = Validator(option, {
				value: [{ rule: 'length', params: [1, 128], label: 'Valor' }]
			});
			setError(valid ? null : prompts[0]);
			if(!valid) return;
			setLoading(true);
			if(option.value_id){
				API.editSurveyValue(option.value_id, option.value).then(res=>{
					if(res.error) return setError(res.message);
					Toast.success('Opción actualizada');
					setForm(q=>({ ...q, values: q.values.map(v=>({ ...v, edit_mode: false })) }));
				}).catch(()=>{
					setError('Hubo un error inesperado actualizando la opción (SVQ-21)');
				}).finally(()=>{
					setLoading(false);
				});
			}else{
				API.createSurveyValue(parseInt(question_id), option.value).then(res=>{
					if(res.error) return setError(res.message);
					Toast.success('Opción agregada');
					var values = [...form.values.map(v=>({ ...v, edit_mode: false, value_id: v.value_id || res.data }))];
					setForm(q=>({ ...q, values }));
				}).catch(()=>{
					setError('Hubo un error inesperado creando la opción (SVQ-22)');
				}).finally(()=>{
					setLoading(false);
				});
			}
		}
	}

	var deleteOption = (option: QuestionValueForm)=>{
		return (setLoading: SetLoading)=>{
			setLoading(true);
			if(option.value_id){
				API.deleteSurveyValue(option.value_id).then(res=>{
					if(res.error) return setError(res.message);
					Toast.success('Opción eliminada');
					setForm(q=>({ ...q, values: q.values.filter(v=>v.value_id !== option.value_id) }));
				}).catch(()=>{
					setError('Hubo un error inesperado eliminando la opción (SVQ-23)');
				}).finally(()=>{
					setLoading(false);
				});
			}else{
				setForm(q=>({ ...q, values: q.values.filter(v=>!!v.value_id) }));
				setLoading(false);
			}
		}
	}

	if(loadError) return <Header text='Error' subtext={loadError} />;
	if(!form) return <Header text='Cargando datos' loading />;

	var onFormChange = bindFormChange(form, setForm);
	var someOptionIsEdit = form.values?.some(v=>v.edit_mode);

	return <>
		<Groupper
			title={IS_CREATE ? 'Crear pregunta' : 'Editar pregunta'}
			style={{ margin: 'auto', marginBottom: 10, marginTop: 20, width: 600 }}
			actions={<div>
				{!IS_CREATE && (
					<Button
						text={form.deleted ? 'Restaurar' : 'Eliminar'}
						basic
						color={form.deleted ? 'green' : 'red'}
						size='small'
						onClick={onDelete}
						style={{ marginRight: 5 }}
					/>
				)}
				<Button text='Guardar' color='black' size='small' onClick={save} />
			</div>}
		>
			{!!error && <Message type='error' list={[error]} style={{ marginBottom: 10 }} />}
			<Input label='Pregunta' placeholder='Pregunta' value={form.question} onChange={onFormChange('question')} fluid />
			<div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
				<Dropdown
					style={{ flex: 1, minWidth: 0 }}
					label='Tipo de pregunta'
					placeholder='Tipo de pregunta'
					value={form.type_id ?? null}
					onValueChange={v=>setForm(f=>({ ...f, type_id: v }))}
					items={[
						{ text: 'Texto', value: SurveyQuestionType.INPUT },
						{ text: 'Texto largo', value: SurveyQuestionType.TEXTAREA },
						{ text: 'Opciones', value: SurveyQuestionType.DROPDOWN },
						{ text: 'Opción múltiple', value: SurveyQuestionType.MULTIPLE },
						{ text: 'Si / No', value: SurveyQuestionType.YESNO },
						{ text: 'Calificación', value: SurveyQuestionType.RATING },
						{ text: 'Fecha', value: SurveyQuestionType.DATE },
						{ text: 'Texto informativo', value: SurveyQuestionType.STATIC },
					]}
				/>
				<Input style={{ flex: 1, minWidth: 0 }} label='Comentario' placeholder='Comentario' value={form.question_comment} onChange={onFormChange('question_comment')} fluid />
			</div>
			<Input label='Valor por defecto' placeholder='Opcional' value={form.default_value} onChange={onFormChange('default_value')} fluid />
			<Checkbox
				checked={!!form.required}
				label='Obligatoria'
				onChange={v=>setForm(f=>({ ...f, required: v }))}
			/>
		</Groupper>
		{!IS_CREATE && surveyTypeHasValues(form.type_id) && (
			<Table
				striped
				style={{ width: 600, margin: 'auto' }}
			>
				<Table.Head
					title='Opciones'
					titleRight={
						<Button disabled={someOptionIsEdit} size='small' iconName='plus-circle' text='Agregar' color='black' onClick={addOption} />
					}
				>
					<Table.Row>
						<Table.Cell header collapsing>ID</Table.Cell>
						<Table.Cell header>Valor</Table.Cell>
						<Table.Cell header collapsing><Icon name='pen' /></Table.Cell>
						<Table.Cell header collapsing><Icon name='remove' /></Table.Cell>
					</Table.Row>
				</Table.Head>
				<Table.Body>
					{(!form.values || form.values.length === 0) ? (
						<Table.Row>
							<Table.Cell colSpan={4} centered>
								No hay opciones en esta pregunta
							</Table.Cell>
						</Table.Row>
					) : form.values.map((a, index)=>(
						<Table.Row key={`sval-${a.value_id}-${index}`}>
							{!a.edit_mode ? (
								<>
									<Table.Cell collapsing>{a.value_id}</Table.Cell>
									<Table.Cell>{a.value}</Table.Cell>
									<Table.Cell collapsing centered>
										<Button
											disabled={someOptionIsEdit}
											iconName='pencil'
											size='small'
											onClick={()=>{
												setForm(q=>({
													...q,
													values: q.values.map(qv=>({
														...qv,
														edit_mode: qv.value_id === a.value_id,
													})),
												}));
											}}
										/>
									</Table.Cell>
									<Table.Cell collapsing centered>
										<Button disabled={someOptionIsEdit} iconName='remove' size='small' onClick={deleteOption(a)} />
									</Table.Cell>
								</>
							) : (
								<>
									<Table.Cell collapsing>{a.value_id}</Table.Cell>
									<Table.Cell>
										<Input
											placeholder='Nueva opción'
											value={a.value}
											onChange={onOptionChange(index, 'value')}
											style={{ margin: 0, width: '66%' }}
										/>
									</Table.Cell>
									<Table.Cell collapsing centered>
										<Button size='small' iconName='check-circle' color='green' onClick={onSaveOption(a)} />
									</Table.Cell>
									<Table.Cell collapsing centered>
										<Button size='small' iconName='remove' onClick={deleteOption(a)} />
									</Table.Cell>
								</>
							)}
						</Table.Row>
					))}
				</Table.Body>
			</Table>
		)}
	</>
}

export default SurveyQuestion;
